import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import MobileHeader from "../../components/MobileHeader";
import BottomNav from "../../components/BottomNav";
import MapView from "../../components/MapView";
import { api, formatApiError, formatCDF, DRC_CENTER, DRC_MAP_ZOOM, CITY_MAP_ZOOM } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Badge, Button, Card, Input, Label } from "../../components/ui-kit";
import { CalendarBlank, FirstAidKit, MapPin, Package, QrCode, ShoppingCart, Storefront, Ticket, Van, UsersThree } from "@phosphor-icons/react";

const LOCALES = {
  fr: "fr-FR",
  en: "en-US",
  ln: "fr-CD",
  sw: "sw-CD",
};

export default function CustomerServices() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [booking, setBooking] = useState({});
  const [selected, setSelected] = useState(null);
  const eventsRef = useRef(null);
  const ticketsRef = useRef(null);
  const locale = LOCALES[(i18n.language || "fr").slice(0, 2)] || "fr-FR";

  const load = useCallback(() => {
    Promise.all([
      api.get("/events", { params: { city_id: user?.city_id } }),
      api.get("/shuttle-tickets"),
    ])
      .then(([e, ticketRes]) => {
        const eventList = Array.isArray(e.data) ? e.data : [];
        setEvents(eventList);
        setTickets(Array.isArray(ticketRes.data) ? ticketRes.data : []);
        setSelected((current) => current || eventList[0] || null);
      })
      .catch((err) => toast.error(formatApiError(err)));
  }, [user?.city_id]);

  useEffect(() => {
    load();
  }, [load]);

  const center = useMemo(() => {
    if (!selected) return DRC_CENTER;
    return { lat: selected.dropoff_lat, lng: selected.dropoff_lng };
  }, [selected]);

  const reserve = async (event) => {
    try {
      const payload = {
        seats: Number(booking[event.id]?.seats || 1),
        passenger_name: booking[event.id]?.passenger_name || user?.name,
        passenger_phone: booking[event.id]?.passenger_phone || user?.phone,
        pickup_note: booking[event.id]?.pickup_note || "",
      };
      const { data } = await api.post(`/events/${event.id}/tickets`, payload);
      toast.success(`${t("services.ticket_reserved")}: ${data.code}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="phone-frame phone-scroll-safe bg-premium-grad">
      <MobileHeader title={t("nav.services")} />
      <div className="space-y-4 px-4 pt-2">
        <Card className="overflow-hidden p-0">
          <div className="h-44 min-[390px]:h-52">
            <MapView
              center={center}
              pickup={selected ? { lat: selected.pickup_lat, lng: selected.pickup_lng } : null}
              destination={selected ? { lat: selected.dropoff_lat, lng: selected.dropoff_lng } : null}
              driverVehicleType="xl"
              height="100%"
              zoom={selected ? CITY_MAP_ZOOM : DRC_MAP_ZOOM}
              showAttribution={false}
            />
          </div>
          <div className="p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone="primary"><Van size={12} weight="bold" /> {t("services.shuttle")}</Badge>
              <Badge tone="success"><Ticket size={12} weight="bold" /> {t("services.ticket")}</Badge>
            </div>
            <h1 className="mt-3 font-heading text-2xl font-black">{t("services.title")}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("services.subtitle")}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <Link
                to="/app/delivery"
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-secondary px-3 py-2 text-[11px] font-black uppercase tracking-widest text-secondary-foreground press-down glow-green"
                data-testid="services-delivery"
              >
                <Package size={16} weight="bold" />
                Livraison colis
              </Link>
              <button
                type="button"
                onClick={() => eventsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full bg-primary px-3 py-2 text-[11px] font-black uppercase tracking-widest text-primary-foreground press-down glow-blue"
                data-testid="services-view-shuttles"
              >
                <Van size={16} weight="bold" />
                {t("services.view_shuttles")}
              </button>
              <button
                type="button"
                onClick={() => ticketsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-2 text-[11px] font-black uppercase tracking-widest text-foreground press-down"
                data-testid="services-my-tickets"
              >
                <Ticket size={16} weight="bold" />
                {t("services.my_tickets")}
              </button>
              <Link
                to="/app/ride"
                className="col-span-2 inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-secondary/30 bg-secondary/15 px-3 py-2 text-[11px] font-black uppercase tracking-widest text-secondary press-down"
                data-testid="services-taxi-now"
              >
                <UsersThree size={16} weight="bold" />
                {t("services.taxi_now")}
              </Link>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Link to="/app/marketplace/restaurants">
            <Card className="h-full p-4 press-down">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-primary/15 text-primary">
              <Storefront size={22} weight="duotone" />
            </div>
            <h2 className="mt-3 font-heading text-lg font-black">Restaurants</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Menus, panier, commande et livraison par chauffeur.
            </p>
            <Badge tone="success" className="mt-3">Commander</Badge>
            </Card>
          </Link>
          <Link to="/app/marketplace/supermarkets">
            <Card className="h-full p-4 press-down">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-secondary/15 text-secondary">
              <ShoppingCart size={22} weight="duotone" />
            </div>
            <h2 className="mt-3 font-heading text-lg font-black">Supermarches</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Produits, courses, panier et livraison a domicile.
            </p>
            <Badge tone="success" className="mt-3">Acheter</Badge>
            </Card>
          </Link>
          <Link to="/app/marketplace/pharmacies" className="col-span-2">
            <Card className="h-full p-4 press-down">
            <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-500/15 text-emerald-300">
              <FirstAidKit size={22} weight="duotone" />
            </div>
            <h2 className="mt-3 font-heading text-lg font-black">Pharmacies</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Produits disponibles, panier unique et livraison par chauffeur/livreur.
            </p>
            <Badge tone="success" className="mt-3">Commander</Badge>
            </Card>
          </Link>
        </div>

        <div ref={eventsRef} className="scroll-mt-24 grid gap-3">
          {events.map((event) => {
            const form = booking[event.id] || {};
            return (
              <Card key={event.id} className="p-4" data-testid={`event-${event.id}`}>
                <button onClick={() => setSelected(event)} className="block w-full text-left">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-primary">{event.city_name}</div>
                      <h2 className="font-heading text-xl font-black">{event.title}</h2>
                      <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-muted-foreground">
                        <span><CalendarBlank size={12} className="mr-1 inline" />{new Date(event.starts_at).toLocaleString(locale)}</span>
                        <span><MapPin size={12} className="mr-1 inline" />{event.venue_name}</span>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-mono text-sm font-black">{formatCDF(event.shuttle_price)}</div>
                      <div className="text-[10px] text-muted-foreground">{t("services.per_seat")}</div>
                    </div>
                  </div>
                </button>
                <div className="mt-3 grid grid-cols-3 gap-2 rounded-2xl bg-white/5 p-3 text-center">
                  <div>
                    <div className="text-[9px] uppercase text-muted-foreground">{t("services.capacity")}</div>
                    <div className="font-bold">{event.capacity}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-muted-foreground">{t("services.departure")}</div>
                    <div className="truncate font-bold">{event.pickup_label || t("services.default_pickup")}</div>
                  </div>
                  <div>
                    <div className="text-[9px] uppercase text-muted-foreground">{t("services.payment")}</div>
                    <div className="font-bold">{t("services.onboard")}</div>
                  </div>
                </div>
                <div className="mt-3 grid gap-2">
                  <div className="grid grid-cols-[96px_1fr] gap-2">
                    <div>
                      <Label>{t("services.seats")}</Label>
                      <Input
                        type="number"
                        min="1"
                        max="8"
                        value={form.seats || 1}
                        onChange={(e) => setBooking((b) => ({ ...b, [event.id]: { ...b[event.id], seats: e.target.value } }))}
                      />
                    </div>
                    <div>
                      <Label>{t("services.passenger_name")}</Label>
                      <Input
                        value={form.passenger_name || ""}
                        placeholder={user?.name}
                        onChange={(e) => setBooking((b) => ({ ...b, [event.id]: { ...b[event.id], passenger_name: e.target.value } }))}
                      />
                    </div>
                  </div>
                  <Input
                    value={form.pickup_note || ""}
                    placeholder={t("services.pickup_placeholder")}
                    onChange={(e) => setBooking((b) => ({ ...b, [event.id]: { ...b[event.id], pickup_note: e.target.value } }))}
                  />
                  <Button onClick={() => reserve(event)} variant="primary" className="w-full" data-testid={`reserve-event-${event.id}`}>
                    {t("services.reserve_ticket")}
                  </Button>
                </div>
              </Card>
            );
          })}
          {events.length === 0 && (
            <Card className="p-6 text-center text-sm text-muted-foreground">
              {t("services.no_events")}
            </Card>
          )}
        </div>

        <div ref={ticketsRef} className="scroll-mt-24">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="font-heading text-xl font-black">{t("services.my_tickets")}</h2>
            <Badge tone="default">{tickets.length}</Badge>
          </div>
          <div className="space-y-2">
            {tickets.map((ticket) => (
              <Card key={ticket.id} className="p-4" data-testid={`ticket-${ticket.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-heading text-lg font-black">{ticket.event_title}</div>
                    <div className="mt-1 font-mono text-sm text-primary">{ticket.code}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{ticket.seats} place(s) - {formatCDF(ticket.amount)}</div>
                  </div>
                  <div className="rounded-2xl bg-white p-2 text-black">
                    <QrCode size={48} weight="bold" />
                  </div>
                </div>
              </Card>
            ))}
            {tickets.length === 0 && (
              <Card className="p-5 text-center text-xs text-muted-foreground">
                {t("services.no_tickets")}
              </Card>
            )}
          </div>
        </div>

        <Link to="/app/ride">
          <Button variant="outline" className="w-full"><UsersThree weight="bold" /> {t("services.back_to_taxi")}</Button>
        </Link>
      </div>
      <BottomNav role="customer" />
    </div>
  );
}
